/* The muscle split's ranked rows. Replaces the canon's stacked bar and its legend — the component
 * header carries the reasoning; this file carries the geometry.
 *
 * The row is a three-column grid rather than a flex line, and that is what makes the readout
 * legible: every name starts at the same x, every track starts and ends at the same x, and every
 * percentage is right-aligned against the same edge. A flex row would let the longest group name
 * decide where the tracks begin, so `Klatka Piersiowa` and `Barki` would draw their bars from
 * different places and the lengths would stop comparing.
 *
 * `--w-pl-track` and `--w-pl-share` are fixed so the NAME column takes whatever is left: the pane
 * is resizable, the two right columns are not, and a track that grew with the window would make
 * two plans look different on two screens.
 *
 * **Every text leaf states its line-height**, and here it is the type step's own fixed line
 * (`--type-control-lh`) rather than `normal`: a row's pitch is what `MuscleSection`'s readout reserve
 * and its skeleton are measured in, so it is a number rather than a font metric.
 *
 * Heights and widths are layout rather than design tokens (see stylelint.config.js); every colour,
 * space, radius and type value below is a token. */

.ranking {
  display: flex;
  flex-direction: column;
  gap: var(--space-5-5px);
  min-width: 0;
}

.row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) var(--w-pl-track) var(--w-pl-share);
  gap: var(--space-9-5px);
  align-items: center;
}

.label {
  overflow: hidden;
  font-size: var(--type-control);
  font-weight: var(--fw-medium);
  line-height: var(--type-control-lh);
  color: var(--text-2);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The leader is the answer to "what is this plan built on", so it is the one row drawn in full ink.
 * It is the FIRST row: the remainder is no longer a row that could outrank it (owner, 4 November
 * 2026), and a tie for first is still one leader, the one the ranking put on top. */
.lead .label {
  font-weight: var(--fw-semibold);
  color: var(--text);
}

/* The unfilled remainder of the track. Faint enough to read as a rail rather than as a second
 * segment — the row already carries one bar and a second one would restate the same number. */
.track {
  height: var(--h-pl-bar);
  overflow: hidden;
  background: var(--bg-inset);
  border-radius: var(--radius-5px);
}

.fill {
  display: block;
  width: var(--row-fill);
  height: 100%;
  background: var(--row-ink);
  border-radius: var(--radius-5px);
}

.percent {
  font-size: var(--type-control);
  font-weight: var(--fw-bold);
  line-height: var(--type-control-lh);
  color: var(--text);
  text-align: right;
  font-variant-numeric: tabular-nums;
}
